import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import { Dropdown } from 'primereact/dropdown';
import { InputText } from 'primereact/inputtext';
import { useEffect, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import classes from './addRowModalClearTime.module.scss';

interface FormData {
  roomTypeId: number;
  cleanTypeId: number;
  outgoingJobTypeId: number;
  incomingJobTypeId: number;
  clearTimeMinutes: number;
}

interface DropdownOption {
  label: string;
  value: any;
}

interface AddRowModalClearTimeProps {
  visible: boolean;
  onHide: () => void;
  onSubmit: (data: FormData) => Promise<void>;
  initialValues?: FormData | null;
  isEdit: boolean;
  roomOptions: DropdownOption[];
  cleanTypeOptions: DropdownOption[];
  outgoingJobTypeOptions: DropdownOption[];
  incomingJobTypeOptions: DropdownOption[];
}

const AddRowModalClearTime = ({
  visible,
  onHide,
  onSubmit,
  initialValues,
  isEdit,
  roomOptions,
  cleanTypeOptions,
  outgoingJobTypeOptions,
  incomingJobTypeOptions
}: AddRowModalClearTimeProps) => {
  const [formData, setFormData] = useState<FormData>({
    roomTypeId: 0,
    cleanTypeId: 0,
    outgoingJobTypeId: 0,
    incomingJobTypeId: 0,
    clearTimeMinutes: 0
  });

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const environment = import.meta.env.VITE_ENVIRONMENT;
  const modalSessionRef = useRef<number>(0);
  const initializedRef = useRef<boolean>(false);

  useEffect(() => {
    if (visible) {
      // Track modal session - increment when modal opens
      if (!initializedRef.current) {
        modalSessionRef.current += 1;
        initializedRef.current = true;

        // Only update form when modal first opens
        if (initialValues) {
          setFormData({ ...initialValues });
        } else {
          // Reset form for new entry
          setFormData({
            roomTypeId: 0,
            cleanTypeId: 0,
            outgoingJobTypeId: 0,
            incomingJobTypeId: 0,
            clearTimeMinutes: 0
          });
        }
        setErrors({});
      }
    } else {
      // Modal closed, reset initialization flag
      initializedRef.current = false;
    }
    // Note: initialValues is intentionally not in dependencies to prevent re-initialization
    // when parent component re-renders after store updates
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible]);

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    // Required fields
    if (!formData.roomTypeId || formData.roomTypeId === 0) {
      newErrors.roomTypeId = 'Room Type is required';
    }
    if (!formData.cleanTypeId || formData.cleanTypeId === 0) {
      newErrors.cleanTypeId = 'Clean Type is required';
    }
    if (!formData.outgoingJobTypeId || formData.outgoingJobTypeId === 0) {
      newErrors.outgoingJobTypeId = 'Outgoing Job Type is required';
    }
    if (!formData.incomingJobTypeId || formData.incomingJobTypeId === 0) {
      newErrors.incomingJobTypeId = 'Incoming Job Type is required';
    }

    // Clear Time validation
    if (formData.clearTimeMinutes === null || formData.clearTimeMinutes === undefined) {
      newErrors.clearTimeMinutes = 'Clear Time is required';
    } else if (formData.clearTimeMinutes < 0) {
      newErrors.clearTimeMinutes = 'Clear Time cannot be negative';
    } else if (formData.clearTimeMinutes > 720) {
      newErrors.clearTimeMinutes = 'Clear Time cannot exceed 720 minutes (12 hours)';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (validateForm()) {
      try {
        setIsSubmitting(true);
        await onSubmit(formData);
        // Close modal after store updates complete
        setTimeout(() => {
          onHide();
        }, 100);
      } catch (error) {
        // Don't close modal on error - error will be shown in toast
        if (environment === 'development') {
          console.error('Error submitting form:', error);
        }
        toast.error('Error submitting form for Clear Time data!');
      } finally {
        setIsSubmitting(false);
      }
    }
  };

  const handleInputChange = (field: keyof FormData, value: any) => {
    setFormData({ ...formData, [field]: value });

    // Clear error when field is updated
    if (errors[field]) {
      setErrors({ ...errors, [field]: '' });
    }
  };

  return (
    <Dialog
      header={isEdit ? 'Edit Clear Time Configuration' : 'Add New Clear Time Configuration'}
      visible={visible}
      onHide={onHide}
      className={classes.modal}
      style={{ width: '500px' }}
    >
      <div className={classes.formContainer}>
        <div className={classes.formGroup}>
          <label>Room Type</label>
          <Dropdown
            value={formData.roomTypeId}
            options={roomOptions}
            onChange={e => handleInputChange('roomTypeId', e.value)}
            placeholder="Select Room"
            className={`${classes.dropdown} ${errors.roomTypeId ? 'p-invalid' : ''}`}
            disabled={isSubmitting}
          />
          {errors.roomTypeId && <small className="p-error">{errors.roomTypeId}</small>}
        </div>

        <div className={classes.formGroup}>
          <label>Outgoing Job Type</label>
          <Dropdown
            value={formData.outgoingJobTypeId}
            options={outgoingJobTypeOptions}
            onChange={e => handleInputChange('outgoingJobTypeId', e.value)}
            placeholder="Select Outgoing Job Type"
            className={`${classes.dropdown} ${errors.outgoingJobTypeId ? 'p-invalid' : ''}`}
            disabled={isSubmitting}
          />
          {errors.outgoingJobTypeId && (
            <small className="p-error">{errors.outgoingJobTypeId}</small>
          )}
        </div>

        <div className={classes.formGroup}>
          <label>Incoming Job Type</label>
          <Dropdown
            value={formData.incomingJobTypeId}
            options={incomingJobTypeOptions}
            onChange={e => handleInputChange('incomingJobTypeId', e.value)}
            placeholder="Select Incoming Job Type"
            className={`${classes.dropdown} ${errors.incomingJobTypeId ? 'p-invalid' : ''}`}
            disabled={isSubmitting}
          />
          {errors.incomingJobTypeId && (
            <small className="p-error">{errors.incomingJobTypeId}</small>
          )}
        </div>

        <div className={classes.formGroup}>
          <label>Clean Type</label>
          <Dropdown
            value={formData.cleanTypeId}
            options={cleanTypeOptions}
            onChange={e => handleInputChange('cleanTypeId', e.value)}
            placeholder="Select Clean Type"
            className={`${classes.dropdown} ${errors.cleanTypeId ? 'p-invalid' : ''}`}
            disabled={isSubmitting}
          />
          {errors.cleanTypeId && <small className="p-error">{errors.cleanTypeId}</small>}
        </div>

        <div className={classes.formGroup}>
          <label>Clear Time (minutes)</label>
          <InputText
            value={formData.clearTimeMinutes.toString()}
            onChange={e =>
              handleInputChange('clearTimeMinutes', parseInt(e.target.value || '0', 10))
            }
            keyfilter="pint"
            placeholder="Enter clear time in minutes"
            className={`${classes.inputText} ${errors.clearTimeMinutes ? 'p-invalid' : ''}`}
            disabled={isSubmitting}
          />
          {errors.clearTimeMinutes && <small className="p-error">{errors.clearTimeMinutes}</small>}
        </div>
      </div>

      <div className={classes.modalFooter}>
        <Button
          label="Cancel"
          icon="pi pi-times"
          onClick={onHide}
          severity="danger"
          text
          disabled={isSubmitting}
        />
        <Button
          label={isSubmitting ? 'Saving...' : 'Save'}
          icon={isSubmitting ? 'pi pi-spinner pi-spin' : 'pi pi-check'}
          onClick={handleSubmit}
          className={classes.saveButton}
          disabled={isSubmitting}
        />
      </div>
    </Dialog>
  );
};

export default AddRowModalClearTime;
